import { AbsoluteFill, Easing, interpolate, useCurrentFrame, useVideoConfig } from 'remotion';
import { z } from 'zod';
import { AustriaMap } from '../components/AustriaMap';
import { SocialSafeZones } from '../components/SocialSafeZones';
import { Typewriter } from '../components/Typewriter';

const FADE_OUT_SECONDS = 0.5;
const SUBTITLE_FADE_IN_SECONDS = 0.5;
const MAP_WIDTH = 220;
const ROW_GAP = 32;
// 1080 (composition) - 90 (left) - 90 (right) - map - gap
const TITLE_AVAILABLE_WIDTH = 1080 - 90 - 90 - MAP_WIDTH - ROW_GAP;
const TITLE_MAX_FONT_SIZE = 84;
const TITLE_LETTER_SPACING = 3;

const AUSTRIAN_STATES = [
  'Burgenland',
  'Kärnten',
  'Niederösterreich',
  'Oberösterreich',
  'Salzburg',
  'Steiermark',
  'Tirol',
  'Vorarlberg',
  'Wien',
] as const;

export const PlaceSchema = z.object({
  title: z.string(),
  marker: z.tuple([z.number(), z.number()]),
  state: z.enum(AUSTRIAN_STATES),
  debug: z.boolean(),
});

export const PlaceComposition: React.FC<z.infer<typeof PlaceSchema>> = ({ title, marker, state, debug }) => {
  const frame = useCurrentFrame();
  const { durationInFrames, fps } = useVideoConfig();

  const fadeOutFrames = FADE_OUT_SECONDS * fps;
  const opacity = interpolate(
    frame,
    [durationInFrames - fadeOutFrames, durationInFrames],
    [1, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  const subtitleOpacity = interpolate(
    frame,
    [0, SUBTITLE_FADE_IN_SECONDS * fps],
    [0, 1],
    {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
      easing: Easing.out(Easing.cubic),
    },
  );

  // Shrink a long title instead of pushing the map aside.
  const titleFontSize = Math.max(
    24,
    Math.min(
      TITLE_MAX_FONT_SIZE,
      (TITLE_AVAILABLE_WIDTH / Math.max(1, title.length) - TITLE_LETTER_SPACING) /
        0.62,
    ),
  );

  return (
    <AbsoluteFill style={{ opacity }}>
      <div
        style={{
          position: 'absolute',
          left: 90,
          right: 90,
          bottom: 140,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'stretch',
          gap: ROW_GAP,
          textTransform: 'uppercase',
        }}
      >
        <div style={{ width: MAP_WIDTH, flexShrink: 0, alignSelf: 'stretch' }}>
          <AustriaMap marker={marker} state={state} />
        </div>
        <div
          style={{
            display: 'flex',
            flex: 1,
            minWidth: 0,
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 9,
          }}
        >
          <Typewriter
            text={title}
            fontSize={titleFontSize}
            fontWeight={200}
            color="#fff"
            cursorColor="#fff"
            style={{ letterSpacing: TITLE_LETTER_SPACING, lineHeight: 1.1 }}
          />
          <div
            style={{
              opacity: subtitleOpacity,
              fontSize: 54,
              fontWeight: 200,
              letterSpacing: 9,
              color: 'rgba(255, 255, 255, 0.85)',
              lineHeight: 1.2,
            }}
          >
            {state}
          </div>
        </div>
      </div>
      {debug && <SocialSafeZones />}
    </AbsoluteFill>
  );
};
